/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { cssMap, jsx } from '@atlaskit/css';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { type JSONNode, JSONTransformer } from '@atlaskit/editor-json-transformer';
import { Fragment, type Node, type Schema } from '@atlaskit/editor-prosemirror/model';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import { token } from '@atlaskit/tokens';
import { fetchAiManagedObject } from '@atlassian/ai-managed-objects/api';

import type { AgentManagedExtensionAttributes } from '../common/agentManagedExtensionPluginType';
import { AgentManagedExtensionView } from '../common/AgentManagedExtensionView';
import { messages } from '../common/messages';
import { getNodeAiManagedObject } from '../common/utils';

type NodeContent = {
	attrs?: Record<string, unknown>;
	content?: NodeContent[];
	marks?: Array<{ attrs?: Record<string, unknown>; type: string }>;
	text?: string;
	type: string;
};

/**
 * Loose `node` shape compatible with both ProseMirror `Node`s (passed by the custom nodeView) and
 * ADF JSON nodes (passed by the manifest's render function). Only `attrs` and `content` are read.
 */
interface Props {
	// Set to true when this block is the current ProseMirror NodeSelection
	isSelected?: boolean;
	node: { attrs?: Record<string, unknown>; content?: unknown };
	providerFactory?: ProviderFactory;
	/** When true, renders only the body content without interactive chrome (hover border, label). */
	readOnly?: boolean;
}

const jsonTransformer = new JSONTransformer();

const styles = cssMap({
	fetchError: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		color: token('color.text.danger'),
		font: token('font.body'),
	},
});

// Rebuild ADF body content as ProseMirror nodes
const createNodesFromContent = (
	content: readonly NodeContent[],
	schema: Schema,
): readonly Node[] => {
	const nodes: Node[] = [];
	for (const node of content) {
		if (node?.type === 'text') {
			const marks = (node.marks ?? [])
				.map((m) => (schema.marks[m.type] ? schema.marks[m.type].create(m.attrs) : null))
				.filter((m): m is NonNullable<typeof m> => m !== null);
			nodes.push(schema.text(node.text ?? '', marks));
		} else if (schema.nodes[node.type] && node.content) {
			const contentNodes = createNodesFromContent(node.content, schema);
			nodes.push(schema.nodes[node.type].create(node.attrs, contentNodes));
		}
	}
	return nodes;
};

/**
 * Error display for when AIMO fetch fails.
 */
function FetchErrorView({ message }: { message: string }): React.ReactElement {
	return (
		<div data-testid="agent-managed-extension-fetch-error" css={styles.fetchError}>
			{message}
		</div>
	);
}

/**
 * Renderer/view-only renderer for the agent-managed bodied extension. Body content is supplied
 * via `node.content` (ADF JSON or PM `Fragment`) and rendered with `<ReactRenderer>`.
 *
 * The component always fetches the AIMO by id so the data is available
 * for rendering (e.g. showing the prompt or status in the block body in the future).
 * If the fetch returns a 404 or errors, an inline error is shown inside the block.
 */
export const AgentManagedBodiedExtensionRenderer = ({
	node,
	providerFactory,
	isSelected,
	readOnly,
}: Props): React.ReactElement | null => {
	const intl = useIntl();
	const attrs = node.attrs as AgentManagedExtensionAttributes | undefined;
	const aimoId = getNodeAiManagedObject(attrs)?.id;
	const fetchErrorMessage = intl.formatMessage(messages.fetchError);

	// ---------------------------------------------------------------------------
	// AIMO fetch — fetches the AIMO data; currently used to detect 404 and
	// show an error. The fetched data will be consumed once rendering supports
	// displaying prompt/status from the AIMO config.
	// ---------------------------------------------------------------------------
	const [fetchError, setFetchError] = useState(false);

	useEffect(() => {
		if (!aimoId) {
			return;
		}

		let cancelled = false;

		setFetchError(false);

		fetchAiManagedObject(aimoId)
			.then((aimo) => {
				if (cancelled) {
					return;
				}
				if (!aimo) {
					// 404 — object not found
					setFetchError(true);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setFetchError(true);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [aimoId]);

	// ---------------------------------------------------------------------------
	// Body content
	// ---------------------------------------------------------------------------
	const doc: DocNode = useMemo(() => {
		let content: JSONNode[];
		if (Array.isArray(node.content)) {
			content = node.content;
		} else if (node.content instanceof Fragment) {
			// Already PM nodes — encode directly to ADF JSON
			content = [];
			node.content.forEach((child) => {
				content.push(jsonTransformer.encodeNode(child));
			});
		} else {
			content = [{ type: 'paragraph' }];
		}
		// This is already valid ADF — just wrap it as a doc
		return { version: 1, type: 'doc', content } as DocNode;
	}, [node.content]);

	if (fetchError) {
		return (
			<AgentManagedExtensionView isSelected={isSelected} readOnly={readOnly}>
				<FetchErrorView message={fetchErrorMessage} />
			</AgentManagedExtensionView>
		);
	}

	return (
		<AgentManagedExtensionView isSelected={isSelected} readOnly={readOnly}>
			<RendererActionsContext>
				<ReactRenderer document={doc} appearance="full-width" dataProviders={providerFactory} />
			</RendererActionsContext>
		</AgentManagedExtensionView>
	);
};
